import {
    Component,
    inject,
    input,
    output,
    computed,
    ChangeDetectionStrategy,
    ViewEncapsulation,
    HostBinding,
} from '@angular/core';
import { FiChecklistHelperService } from '@fi-app/shared/checklist/fi-checklist-helper.service';
import { ICheckpointGroup, ICheckpointV2 } from '@fi-shared/checklist/checklist-v2';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import { FiChecklistStoreService } from '@fi-shared/checklist/fi-checklist-store.service';
import { isDefined } from '@fi-app/shared/utils/is-defined';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { AppTextService } from '@fi-shared/app-text';
import { CheckpointType } from '@fi-shared/checklist/checklist-enum';
import { FiTooltipButtonIconColors } from '@fi-shared/fi-icon-tooltip-button/fi-icon-tooltip-button.component';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
    selector: 'fi-checkpoint-group-item',
    templateUrl: './checkpoint-group-item.component.html',
    styleUrls: ['./checkpoint-group-item.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-checkpoint-group-item' },
    standalone: false,
})
export class FiCheckpointGroupItemComponent {
    private fiContentLanguageSharedService = inject(FiContentLanguageSharedService);
    private fiChecklistStoreService = inject(FiChecklistStoreService);
    private fiChecklistHelperService = inject(FiChecklistHelperService);
    private appTextService = inject(AppTextService);

    checkpoint = input<ICheckpointV2>();
    checkpointGroup = input<ICheckpointGroup>();
    isComplianceThresholdEnabled = input<boolean>();
    navigateToEditCheckpoint = output<void>();
    copyCheckpoint = output<void>();
    confirmCheckpointRemove = output<void>();

    @HostBinding('hidden') get isHidden() {
        return !this.checkpointName();
    }

    protected checkpointActions = computed(() => {
        return [
            {
                isIconVisible: true,
                tooltipClass: 'fi-rounded-white-tooltip',
                iconHoverColor: FiTooltipButtonIconColors.Branded,
                iconClass: this.getCheckpointAnswerTypeIcon(),
                tooltipText: this.getCheckpointAnswerTypeTooltip(),
                onIconClick: () => this.navigateToEditCheckpoint.emit(),
            },
            {
                isIconVisible: true,
                tooltipClass: 'fi-rounded-white-tooltip',
                iconHoverColor: FiTooltipButtonIconColors.Branded,
                iconClass: 'fa-pencil',
                tooltipText: this.appTextService.get('checklist.checkpoint_edit'),
                onIconClick: () => this.navigateToEditCheckpoint.emit(),
            },
            {
                isIconVisible: true,
                tooltipClass: 'fi-rounded-white-tooltip',
                iconHoverColor: FiTooltipButtonIconColors.Branded,
                iconClass: 'fa-clone',
                tooltipText: this.appTextService.get('checklist.checkpoint_copy'),
                onIconClick: () => this.copyCheckpoint.emit(),
            },
            {
                isIconVisible: true,
                tooltipClass: 'fi-rounded-white-tooltip',
                iconHoverColor: FiTooltipButtonIconColors.Branded,
                iconClass: 'fa-trash',
                tooltipText: this.appTextService.get('checklist.checkpoint_delete'),
                onIconClick: () => this.confirmCheckpointRemove.emit(),
            },
        ];
    });

    protected isCheckpointScoringBadgeShown = computed(() => {
        return (
            this.isComplianceThresholdEnabled() &&
            this.checkpointGroup().Checkpoints.some((checkpoint) => checkpoint.Scoring)
        );
    });

    private selectedLanguageId = toSignal(this.fiChecklistStoreService.selectedLanguageIdChange$);

    protected checkpointName = computed(() => {
        return this.getCurrentContentLanguageLabelModelValue() || this.getPrioritizedContentLanguageLabelValue();
    });

    private getPrioritizedContentLanguageLabelValue = computed(() => {
        return this.fiContentLanguageSharedService.getPrioritizedContentLanguageLabelValue({
            contentLanguageLabelModels: this.checkpoint().Names,
            currentSelectedLanguageId: this.selectedLanguageId(),
            isLanguageNameShown: true,
        });
    });

    protected getCurrentContentLanguageLabelModelValue = computed(() => {
        const selectedLanguageId = this.selectedLanguageId();
        return this.fiChecklistHelperService.getCurrentContentLanguageLabelModel(this.checkpoint().Names);
    });

    protected getCheckpointScoring = computed(() => {
        const checkpoint = this.checkpoint();
        if (!isDefined(checkpoint.ScoringData)) {
            return 0;
        }

        return checkpoint.ScoringData.OkPts + checkpoint.ScoringData.NotOkPts + checkpoint.ScoringData.NotFilledOutPts;
    });

    private getCheckpointAnswerTypeIcon = computed(() => {
        return this.isStatusCheckCheckpoint() ? 'fa-eye' : 'fa-balance-scale';
    });

    private getCheckpointAnswerTypeTooltip = computed(() => {
        return this.appTextService.get(
            this.isStatusCheckCheckpoint() ? 'common.status' : 'checklist.checkpoint_type_measurement',
        );
    });

    private isStatusCheckCheckpoint = computed(() => {
        const checkpoint = this.checkpoint();
        return checkpoint.Type === CheckpointType.Status;
    });

    protected readonly FiInfoIconMode = FiInfoIconMode;
}
